import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip, ChipBackgroundColor } from "@amzn/stencil-react-components/chip";
import {
  Expander,
  ExpanderChevronPosition,
  ExpanderTitleRendererParams,
} from "@amzn/stencil-react-components/expander";
import { Col, GridContainer, GridItem, Hr, Row } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent, RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { Text } from "@amzn/stencil-react-components/text";
import { IconGlobe, IconPadlock, IconPeople } from "@amzn/stencil-react-icons";
import ListModalButton from "common/components/ListModal/ListModalButton";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useLists } from "hooks/ListsContext";
import { ListModel, Visibility } from "lib/models/list-models";
import { ItemModel } from "lib/services/catalogical-models";
import React, { FC } from "react";

import { ListItem } from "./ListItem";

const normalizeListItem = (item: ItemModel): ListItem => ({
  name: item.name || item.title,
  description: item.shortDescription || item.description,
  logoUrl: item.logoUrl,
  taxonomyId: item.taxonomyId,
});

export const List: FC<ListModel> = ({
  name,
  description,
  visibility,
  allowedDomains,
  itemCount,
  fullItems,
  listId,
}) => {
  const { deleteList } = useLists();
  const { addMessageBanner } = useMessageBannersBox();

  const handleConfirmDelete = async (close: () => void) => {
    try {
      await deleteList(listId);
      addMessageBanner(getString("lists.deleteSuccess"), MessageBannerType.Success);
      close();
    } catch (error) {
      addMessageBanner(getString("lists.deleteError"), MessageBannerType.Error);
      close();
    }
  };

  const renderDeleteModal = ({ close }: RenderModalFunctionParams) => (
    <ModalContent
      titleText={getString("lists.deleteConfirmation.title")}
      buttons={[
        <Button key="cancel" variant={ButtonVariant.Secondary} onClick={close}>
          {getString("lists.deleteConfirmation.cancel")}
        </Button>,
        <Button key="confirm" variant={ButtonVariant.Primary} onClick={() => handleConfirmDelete(close)}>
          {getString("lists.deleteConfirmation.confirm")}
        </Button>,
      ]}
    >
      <Text>{getString("lists.deleteConfirmation.message", { name })}</Text>
    </ModalContent>
  );

  const renderVisibility = () => {
    switch (visibility) {
      case Visibility.PRIVATE:
        return (
          <Chip icon={<IconPadlock />} backgroundColor={ChipBackgroundColor.Purple}>
            {getString("lists.visibility.private")}
          </Chip>
        );
      case Visibility.PUBLIC:
        return (
          <Chip icon={<IconPeople />} backgroundColor={ChipBackgroundColor.Blue}>
            {getString("lists.visibility.public")}
          </Chip>
        );
      case Visibility.SHARED:
        return (
          <Chip icon={<IconGlobe />} backgroundColor={ChipBackgroundColor.Green}>
            {getString("lists.visibility.shared")}
          </Chip>
        );
      default:
        return visibility;
    }
  };

  const renderTitle = ({ titleText, toggle }: ExpanderTitleRendererParams) => {
    return (
      <Row width="100%" justifyContent="space-between">
        <Col onClick={toggle} data-testid="list-expander-trigger">
          <Text fontSize="T300" fontWeight="bold" color="neutral90" data-testid="list-name">
            {titleText}
          </Text>
          <Text fontSize="T200" color="neutral70" data-testid="list-description">
            {description}
          </Text>
        </Col>
        <Row alignItems="center" gridGap="S300">
          <Chip data-testid="list-item-count">{getString("lists.itemCount", { itemCount })}</Chip>
          <div data-testid="list-visibility">{renderVisibility()}</div>
          <WithModal renderModal={renderDeleteModal}>
            {({ open }) => (
              <Button variant={ButtonVariant.Secondary} onClick={open} data-testid="list-delete-button">
                {getString("lists.deleteList")}
              </Button>
            )}
          </WithModal>
          <ListModalButton
            initialData={{ name, description, visibility, allowedDomains }}
            listId={listId}
            isEdit={true}
            variant={ButtonVariant.Primary}
          />
        </Row>
      </Row>
    );
  };

  return (
    <div data-testid="list-container">
      <Expander
        renderTitle={renderTitle}
        titleText={name}
        chevronPosition={ExpanderChevronPosition.Leading}
        data-testid="list-expander"
      >
        <GridContainer data-testid="list-items-grid">
          {fullItems ? (
            fullItems.map((item, index) => (
              <GridItem lg={4} md={4} key={`${item.taxonomyId}-${index}`}>
                <ListItem listId={listId} item={normalizeListItem(item)} />
              </GridItem>
            ))
          ) : (
            <GridItem lg={4} md={4}>
              <Text>No Items</Text>
            </GridItem>
          )}
        </GridContainer>
      </Expander>
      <Hr />
    </div>
  );
};
